import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useCallback } from 'react';

export interface Agent {
  id: string;
  slug: string;
  name: string;
  prompt: string;
  description: string;
  model: string | null;
  rag_folders: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface Document {
  id: string;
  name: string;
  file_type: 'pdf' | 'docx' | 'txt' | 'md';
  source: 'upload' | 'config';
  created_at: string;
  deleted_at: string | null;
  max_versions: number | null;
  folder: string | null;
  versions?: DocumentVersion[];
  ingestion_job_id?: string;
}

export interface DocumentVersion {
  id: string;
  document_id: string;
  version_number: number;
  file_size: number;
  created_at: string;
  deleted_at: string | null;
}

export interface IngestionJob {
  id: string;
  document_version_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string | null;
  chunks_created: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export function useGenaiBuilderApi() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const getBaseUrl = useCallback(async () => {
    return discoveryApi.getBaseUrl('aws-genai');
  }, [discoveryApi]);

  // ---- Agents ----

  const listAgents = useCallback(async (): Promise<Agent[]> => {
    const base = await getBaseUrl();
    const res = await fetchApi.fetch(`${base}/agents`);
    if (!res.ok) throw new Error(`Failed to list agents: ${res.statusText}`);
    return res.json();
  }, [fetchApi, getBaseUrl]);

  const getAgent = useCallback(async (id: string): Promise<Agent> => {
    const base = await getBaseUrl();
    const res = await fetchApi.fetch(`${base}/agents/${id}`);
    if (!res.ok) throw new Error(`Failed to get agent: ${res.statusText}`);
    return res.json();
  }, [fetchApi, getBaseUrl]);

  const createAgent = useCallback(
    async (data: Pick<Agent, 'slug' | 'name' | 'prompt' | 'description' | 'model' | 'rag_folders'>): Promise<Agent> => {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(`${base}/agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || `Failed to create agent: ${res.statusText}`);
      }
      return res.json();
    },
    [fetchApi, getBaseUrl]
  );

  const updateAgent = useCallback(
    async (
      id: string,
      data: Partial<Pick<Agent, 'name' | 'prompt' | 'description' | 'model' | 'rag_folders'>>
    ): Promise<Agent> => {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(`${base}/agents/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Failed to update agent: ${res.statusText}`);
      return res.json();
    },
    [fetchApi, getBaseUrl]
  );

  const deleteAgent = useCallback(async (id: string): Promise<void> => {
    const base = await getBaseUrl();
    const res = await fetchApi.fetch(`${base}/agents/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`Failed to delete agent: ${res.statusText}`);
  }, [fetchApi, getBaseUrl]);

  const restoreAgent = useCallback(async (id: string): Promise<Agent> => {
    const base = await getBaseUrl();
    const res = await fetchApi.fetch(`${base}/agents/${id}/restore`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error(`Failed to restore agent: ${res.statusText}`);
    return res.json();
  }, [fetchApi, getBaseUrl]);

  // ---- Documents ----

  const listDocuments = useCallback(async (): Promise<Document[]> => {
    const base = await getBaseUrl();
    const res = await fetchApi.fetch(`${base}/documents`);
    if (!res.ok) throw new Error(`Failed to list documents: ${res.statusText}`);
    return res.json();
  }, [fetchApi, getBaseUrl]);

  const getDocument = useCallback(async (id: string): Promise<Document> => {
    const base = await getBaseUrl();
    const res = await fetchApi.fetch(`${base}/documents/${id}`);
    if (!res.ok) throw new Error(`Failed to get document: ${res.statusText}`);
    return res.json();
  }, [fetchApi, getBaseUrl]);

  const uploadDocument = useCallback(
    async (data: {
      name: string;
      file_type: string;
      file: File;
      folder?: string;
    }): Promise<Document> => {
      const base = await getBaseUrl();
      const form = new FormData();
      form.append('name', data.name);
      form.append('file_type', data.file_type);
      form.append('file', data.file);
      if (data.folder) form.append('folder', data.folder);
      const res = await fetchApi.fetch(`${base}/documents/upload`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        if (res.status === 413) throw new Error('File too large. Maximum upload size is 50 MB.');
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to upload document: ${res.statusText}`);
      }
      return res.json();
    },
    [fetchApi, getBaseUrl]
  );

  const deleteDocument = useCallback(async (id: string): Promise<void> => {
    const base = await getBaseUrl();
    const res = await fetchApi.fetch(`${base}/documents/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`Failed to delete document: ${res.statusText}`);
  }, [fetchApi, getBaseUrl]);

  const restoreDocument = useCallback(async (id: string): Promise<Document> => {
    const base = await getBaseUrl();
    const res = await fetchApi.fetch(`${base}/documents/${id}/restore`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error(`Failed to restore document: ${res.statusText}`);
    return res.json();
  }, [fetchApi, getBaseUrl]);

  const updateDocumentFolder = useCallback(
    async (id: string, folder: string | null): Promise<Document> => {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(`${base}/documents/${id}/folder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder }),
      });
      if (!res.ok) throw new Error(`Failed to update folder: ${res.statusText}`);
      return res.json();
    },
    [fetchApi, getBaseUrl]
  );

  const getIngestionJobs = useCallback(
    async (documentId: string): Promise<IngestionJob[]> => {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(`${base}/documents/${documentId}/ingestion-jobs`);
      if (!res.ok) throw new Error(`Failed to get ingestion jobs: ${res.statusText}`);
      return res.json();
    },
    [fetchApi, getBaseUrl]
  );

  // ---- Associations ----

  const listAgentDocuments = useCallback(
    async (agentId: string): Promise<Document[]> => {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(`${base}/agents/${agentId}/documents`);
      if (!res.ok) throw new Error(`Failed to list agent documents: ${res.statusText}`);
      return res.json();
    },
    [fetchApi, getBaseUrl]
  );

  const associateDocument = useCallback(
    async (agentId: string, documentId: string): Promise<void> => {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(
        `${base}/agents/${agentId}/documents/${documentId}`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || `Failed to associate document: ${res.statusText}`);
      }
    },
    [fetchApi, getBaseUrl]
  );

  const removeDocumentAssociation = useCallback(
    async (agentId: string, documentId: string): Promise<void> => {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(
        `${base}/agents/${agentId}/documents/${documentId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error(`Failed to remove association: ${res.statusText}`);
    },
    [fetchApi, getBaseUrl]
  );

  const listDocumentVersions = useCallback(
    async (documentId: string): Promise<DocumentVersion[]> => {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(`${base}/documents/${documentId}/versions`);
      if (!res.ok) throw new Error(`Failed to list versions: ${res.statusText}`);
      return res.json();
    },
    [fetchApi, getBaseUrl]
  );

  const uploadDocumentVersion = useCallback(
    async (
      documentId: string,
      data: { file: File; file_type: string }
    ): Promise<{ version: DocumentVersion; ingestion_job_id: string }> => {
      const base = await getBaseUrl();
      const form = new FormData();
      form.append('file_type', data.file_type);
      form.append('file', data.file);
      const res = await fetchApi.fetch(`${base}/documents/${documentId}/versions`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        if (res.status === 413) throw new Error('File too large. Maximum upload size is 50 MB.');
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to upload version: ${res.statusText}`);
      }
      return res.json();
    },
    [fetchApi, getBaseUrl]
  );

  const restoreDocumentVersion = useCallback(
    async (
      documentId: string,
      versionId: string
    ): Promise<{ version: DocumentVersion; ingestion_job_id: string }> => {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(
        `${base}/documents/${documentId}/versions/${versionId}/restore`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(`Failed to restore version: ${res.statusText}`);
      return res.json();
    },
    [fetchApi, getBaseUrl]
  );

  const setDocumentRetention = useCallback(
    async (documentId: string, maxVersions: number | null): Promise<Document> => {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(`${base}/documents/${documentId}/retention`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ max_versions: maxVersions }),
      });
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || `Failed to set retention: ${res.statusText}`);
      }
      return res.json();
    },
    [fetchApi, getBaseUrl]
  );

  const getConfigSlugs = useCallback(async (): Promise<string[]> => {
    const base = await getBaseUrl();
    const res = await fetchApi.fetch(`${base}/config-slugs`);
    if (!res.ok) return [];
    const body = await res.json();
    return body.slugs ?? [];
  }, [fetchApi, getBaseUrl]);

  const getDocumentContentUrl = useCallback(async (id: string): Promise<string> => {
    const base = await getBaseUrl();
    return `${base}/documents/${id}/content`;
  }, [getBaseUrl]);

  return {
    listAgents,
    getAgent,
    createAgent,
    updateAgent,
    deleteAgent,
    restoreAgent,
    listDocuments,
    getDocument,
    uploadDocument,
    deleteDocument,
    restoreDocument,
    updateDocumentFolder,
    getIngestionJobs,
    listDocumentVersions,
    uploadDocumentVersion,
    restoreDocumentVersion,
    setDocumentRetention,
    getConfigSlugs,
    getDocumentContentUrl,
    listAgentDocuments,
    associateDocument,
    removeDocumentAssociation,
  };
}
